import React, { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  formatDateTime,
  formatJson,
  getOutcomeStyle,
  getStatusStyle,
  isFalseBreakPattern,
} from './streakAuditUtils';

function OutcomeBadge({ outcome }) {
  if (!outcome) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const style = getOutcomeStyle(outcome);
  return (
    <Chip
      label={outcome}
      size="small"
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
}

function StatusBadge({ status }) {
  const style = getStatusStyle(status);
  return (
    <Chip
      label={status}
      size="small"
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, textTransform: 'capitalize' }}
    />
  );
}

function TimelineDetailRow({ day }) {
  const { events, dailyActivity, streakDecision, debugLog, issues } = day;

  return (
    <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
      <Typography variant="subtitle2" gutterBottom>
        Details for {day.localDate}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Daily activity created</Typography>
          <Typography variant="body2">{formatDateTime(dailyActivity?.createdAt)}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Event types</Typography>
          <Typography variant="body2">
            {(events?.types || []).join(', ') || '—'}
          </Typography>
        </Box>
        {events?.firstAppVisitAt && (
          <Box>
            <Typography variant="caption" color="text.secondary">First APP visit</Typography>
            <Typography variant="body2">{formatDateTime(events.firstAppVisitAt)}</Typography>
          </Box>
        )}
        {events?.firstEventAt && events?.firstEventType !== 'APP_VISIT' && events?.firstEventType !== 'app_open' && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              First event ({events.firstEventType})
            </Typography>
            <Typography variant="body2">{formatDateTime(events.firstEventAt)}</Typography>
          </Box>
        )}
      </Box>

      {streakDecision?.metadata && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">Streak decision metadata</Typography>
          <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: '#fff' }}>
            <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {formatJson(streakDecision.metadata)}
            </Typography>
          </Paper>
        </Box>
      )}

      {debugLog?.context && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">Debug log context</Typography>
          <Paper variant="outlined" sx={{ p: 1, mt: 0.5, bgcolor: '#fff' }}>
            <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {formatJson(debugLog.context)}
            </Typography>
          </Paper>
        </Box>
      )}

      {issues?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">Issues</Typography>
          {issues.map((issue, idx) => (
            <Typography key={idx} variant="body2" sx={{ mt: 0.5 }}>
              <strong>{issue.issueType}:</strong> {issue.description}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

function StreakTimelineTable({ timeline, showInactiveDays, onToggleInactive }) {
  const [expandedRows, setExpandedRows] = useState({});

  const visibleTimeline = showInactiveDays
    ? timeline
    : timeline.filter((d) => d.status !== 'inactive');

  const toggleRow = (localDate) => {
    setExpandedRows((prev) => ({ ...prev, [localDate]: !prev[localDate] }));
  };

  if (!timeline?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        No timeline data for this range.
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showInactiveDays}
              onChange={(e) => onToggleInactive(e.target.checked)}
            />
          }
          label="Show inactive days"
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Daily</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>APP visits</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>First event</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Audit</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Debug</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Issues</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleTimeline.map((day) => {
              const isExpanded = Boolean(expandedRows[day.localDate]);
              const highlight = isFalseBreakPattern(day);
              const firstType = day.events?.firstEventType;
              const isNonAppFirst =
                firstType && firstType !== 'APP_VISIT' && firstType !== 'app_open';

              return (
                <React.Fragment key={day.localDate}>
                  <TableRow
                    hover
                    sx={{
                      bgcolor: highlight ? '#fff8e1' : undefined,
                      '& > td': { borderBottom: isExpanded ? 0 : undefined },
                    }}
                  >
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleRow(day.localDate)}>
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{day.localDate}</TableCell>
                    <TableCell><StatusBadge status={day.status} /></TableCell>
                    <TableCell align="center">
                      {day.dailyActivity?.exists ? '✓' : '—'}
                    </TableCell>
                    <TableCell align="right">{day.events?.appVisitCount ?? 0}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: isNonAppFirst ? 700 : 400, color: isNonAppFirst ? '#e65100' : 'inherit' }}
                      >
                        {firstType || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{day.streakDecision?.decision || '—'}</TableCell>
                    <TableCell><OutcomeBadge outcome={day.debugLog?.outcome} /></TableCell>
                    <TableCell align="center">
                      {day.issues?.length > 0 ? (
                        <Chip label={day.issues.length} size="small" color="warning" />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ py: 0, px: 0 }}>
                      <Collapse in={isExpanded}>
                        <TimelineDetailRow day={day} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default StreakTimelineTable;
